import { tierRank, tierIncludesFeature } from './instructorPlanTier';

/**
 * Reads org-scoped meter caps from the instructor entitlements API contract.
 */

/** Minimum tier slug for team invite — matches instructor_plan_entitlement matrix. */
export const INVITE_TEAM_MIN_TIER = 'organization';

export const INSTRUCTOR_METERS = {
  courses: 'inst.courses',
  students: 'inst.students',
  assessments: 'inst.assessments',
};

/**
 * Returns limit / used / remaining for one meter key (null limit = unlimited).
 */
export function readMeter(entitlements, meterKey) {
  if (!entitlements?.meters) {
    return { limit: null, used: 0, remaining: null };
  }
  const raw = entitlements.meters[meterKey];
  if (!raw || typeof raw !== 'object') {
    return { limit: null, used: 0, remaining: null };
  }
  const limit = raw.limit == null ? null : Number(raw.limit);
  const used = Number(raw.used) || 0;
  const remaining = raw.remaining == null
    ? (limit == null ? null : Math.max(0, limit - used))
    : Number(raw.remaining);
  return { limit, used, remaining };
}

/**
 * True when the org has hit or exceeded a finite meter cap.
 */
export function isMeterAtCap(entitlements, meterKey) {
  const { limit, used } = readMeter(entitlements, meterKey);
  if (limit == null) return false;
  if (limit === 0) return true;
  return used >= limit;
}

/**
 * Maps entitlements meters to legacy package limit fields used by dashboard UI.
 */
export function planLimitsFromEntitlements(entitlements) {
  const courses = readMeter(entitlements, INSTRUCTOR_METERS.courses);
  const students = readMeter(entitlements, INSTRUCTOR_METERS.students);
  const assessments = readMeter(entitlements, INSTRUCTOR_METERS.assessments);
  const tier = entitlements?.tier || null;
  return {
    numberOfCourses: courses.limit,
    numberOfStudents: students.limit,
    numberOfAssessments: assessments.limit,
    courseUsed: courses.used,
    studentUsed: students.used,
    assessmentUsed: assessments.used,
    tier,
    name: tier,
  };
}

/**
 * Keeps chat assistant package validation in sync with entitlements meters.
 */
export function cachePackageLimits(limits) {
  try {
    localStorage.setItem(
      'studyak_package_limits',
      JSON.stringify({
        courses: limits.numberOfCourses,
        students: limits.numberOfStudents,
        assessments: limits.numberOfAssessments,
        name: limits.name || limits.tier || '',
      }),
    );
  } catch (error) {
    /* ignore storage errors */
  }
}

/**
 * Builds a 402-shaped body for client-side pre-checks before POST.
 */
export function meterGateBody(meterKey, entitlements) {
  const { limit } = readMeter(entitlements, meterKey);
  return {
    error: 'limit_reached',
    meter: meterKey,
    limit,
    current_tier: entitlements?.tier || null,
    upgrade_to: entitlements?.tier === 'starter' ? 'professional' : 'organization',
  };
}

/** True when the viewer may open Stripe Connect onboarding (org billing admin). */
export function canShowStripeConnect(entitlements) {
  return entitlements?.ui?.show_stripe !== false;
}

/** True when Billing should show the org-level in-app purchase master switch. */
export function canShowInAppPurchaseBillingSetting(entitlements) {
  return entitlements?.ui?.show_billing === true;
}

/** True when the org plan tier includes in-app purchase configuration. */
export function hasInAppPurchasePlanFeature(entitlements) {
  return Boolean(entitlements?.features?.in_app_purchase_config);
}

/** Org-level in-app purchase master switch from Billing (defaults to enabled). */
export function isOrgInAppPurchaseEnabled(entitlements) {
  if (entitlements?.ui?.in_app_purchase_enabled === false) {
    return false;
  }
  return true;
}

/** True when course/assessment settings may show in-app purchase pricing toggles. */
export function canConfigureInAppPurchase(entitlements) {
  return hasInAppPurchasePlanFeature(entitlements)
    && isOrgInAppPurchaseEnabled(entitlements);
}

/**
 * Resolves a gated feature. An explicit server value always wins — including an explicit
 * `false`, which is how a role restriction is expressed. Only a key the payload never mentions
 * falls back to the tier ladder, so an unseeded plan row can't revoke a feature the tier
 * already paid for.
 */
function hasPlanFeature(entitlements, featureKey) {
  const explicit = entitlements?.features?.[featureKey];
  if (explicit !== undefined && explicit !== null) {
    return Boolean(explicit);
  }
  return tierIncludesFeature(entitlements?.tier, featureKey);
}

/** True when tier ∩ role includes AI course builder (admin and invited instructor). */
export function canUseAiCourseBuilder(entitlements) {
  return hasPlanFeature(entitlements, 'ai_course_builder');
}

/** True when SCORM / video training mode is included (Growth+). */
export function canUseScormTraining(entitlements) {
  return hasPlanFeature(entitlements, 'scorm_training');
}

/** True when programmatic API access is included (Enterprise). */
export function canUseApiAccess(entitlements) {
  return hasPlanFeature(entitlements, 'api_access');
}

/** True when custom AI tutor persona is included (Enterprise). */
export function canUseCustomAiTutorPersona(entitlements) {
  return hasPlanFeature(entitlements, 'custom_ai_tutor_persona');
}

/** True when the org may invite or manage team members (Organization tier+). */
export function canInviteTeamMembers(entitlements) {
  if (entitlements?.ui?.show_invite === true) {
    return true;
  }
  if (Boolean(entitlements?.features?.invite_team_members)) {
    return true;
  }
  return tierRank(entitlements?.tier) >= tierRank(INVITE_TEAM_MIN_TIER);
}

/** Builds a 402-shaped body for client-side team-management pre-checks. */
export function inviteTeamMembersGateBody(entitlements) {
  return {
    error: 'feature_locked',
    feature: 'invite_team_members',
    current_tier: entitlements?.tier || null,
    upgrade_to: INVITE_TEAM_MIN_TIER,
    message: 'Upgrade required',
  };
}

/** Builds a 402-shaped body for client-side AI builder pre-checks. */
export function aiCourseBuilderGateBody(entitlements) {
  return {
    error: 'feature_locked',
    feature: 'ai_course_builder',
    current_tier: entitlements?.tier || null,
    upgrade_to: 'growth',
    message: 'Upgrade required',
  };
}

/** Builds a 402-shaped body for SCORM training mode. */
export function scormTrainingGateBody(entitlements) {
  return {
    error: 'feature_locked',
    feature: 'scorm_training',
    current_tier: entitlements?.tier || null,
    upgrade_to: 'growth',
    message: 'Upgrade required',
  };
}

/** Builds a 402-shaped body for API access. */
export function apiAccessGateBody(entitlements) {
  return {
    error: 'feature_locked',
    feature: 'api_access',
    current_tier: entitlements?.tier || null,
    upgrade_to: 'enterprise',
    message: 'Upgrade required',
  };
}

/** Builds a 402-shaped body for custom AI tutor persona. */
export function customAiTutorPersonaGateBody(entitlements) {
  return {
    error: 'feature_locked',
    feature: 'custom_ai_tutor_persona',
    current_tier: entitlements?.tier || null,
    upgrade_to: 'enterprise',
    message: 'Upgrade required',
  };
}
